// Coordinator — Daily Overview (Multi-Doctor)
// Shows today's appointments across all doctors with live API data

import { useState, useEffect } from 'react';
import { Calendar, Users, Clock, CheckCircle, XCircle, AlertTriangle, Video, Filter } from 'lucide-react';

interface Doctor {
    id: string;
    firstName: string;
    lastName: string;
    specialty: string | null;
    calendarColor: string;
}

interface AppointmentItem {
    id: string;
    scheduledStart: string;
    scheduledEnd: string;
    status: string;
    serviceType: string;
    appointmentType: string;
    chiefComplaint?: string;
    patient: { id: string; firstName: string; lastName: string };
    doctor: { id: string; firstName: string; lastName: string; specialty: string | null; calendarColor: string };
}

import { API_BASE } from '../config/api';

const statusConfig: Record<string, { label: string; cls: string; icon: typeof CheckCircle }> = {
    booked: { label: 'Agendada', cls: 'badge-neutral', icon: Clock },
    confirmed: { label: 'Confirmada', cls: 'badge-primary', icon: CheckCircle },
    arrived: { label: 'Llegó', cls: 'badge-warning', icon: Users },
    in_progress: { label: 'En consulta', cls: 'badge-primary', icon: AlertTriangle },
    completed: { label: 'Completada', cls: 'badge-success', icon: CheckCircle },
    cancelled: { label: 'Cancelada', cls: 'badge-danger', icon: XCircle },
    no_show: { label: 'No asistió', cls: 'badge-danger', icon: XCircle },
};

export function DailyOverviewPage() {
    const [appointments, setAppointments] = useState<AppointmentItem[]>([]);
    const [doctors, setDoctors] = useState<Doctor[]>([]);
    const [selectedDoctorId, setSelectedDoctorId] = useState<string>('all');
    const [loading, setLoading] = useState(true);

    const dateStr = new Date().toISOString().split('T')[0];

    useEffect(() => {
        loadData();
    }, []);

    async function loadData() {
        setLoading(true);
        try {
            const [unifiedRes] = await Promise.all([
                fetch(`${API_BASE}/appointments/unified?date=${dateStr}`).then(r => r.json()),
            ]);

            const data = unifiedRes.data || [];
            setDoctors(data.map((d: Doctor & { appointments: AppointmentItem[] }) => ({
                id: d.id, firstName: d.firstName, lastName: d.lastName,
                specialty: d.specialty, calendarColor: d.calendarColor,
            })));

            // Flatten all appointments
            const allAppts: AppointmentItem[] = [];
            for (const doc of data) {
                for (const appt of doc.appointments) {
                    allAppts.push({ ...appt, doctor: { id: doc.id, firstName: doc.firstName, lastName: doc.lastName, specialty: doc.specialty, calendarColor: doc.calendarColor } });
                }
            }
            allAppts.sort((a, b) => new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime());
            setAppointments(allAppts);
        } catch {
            setAppointments([]);
            setDoctors([]);
        } finally {
            setLoading(false);
        }
    }

    const filtered = selectedDoctorId === 'all'
        ? appointments
        : appointments.filter(a => a.doctor.id === selectedDoctorId);

    const confirmed = filtered.filter(a => a.status === 'confirmed').length;
    const completed = filtered.filter(a => a.status === 'completed').length;
    const cancelled = filtered.filter(a => a.status === 'cancelled').length;
    const telehealth = filtered.filter(a => a.appointmentType === 'telehealth').length;

    // Per-doctor counts
    const doctorCounts = doctors.map(d => ({
        ...d,
        count: appointments.filter(a => a.doctor.id === d.id).length,
    }));

    return (
        <div className="fade-in">
            <h1 className="page-title">
                <Calendar size={22} style={{ display: 'inline', marginRight: 8 }} />
                Vista del Día — {new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
            </h1>

            <div className="stats-row">
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#DBEAFE', color: '#1D4ED8' }}><Calendar size={20} /></div>
                    <div className="stat-value">{loading ? '...' : filtered.length}</div>
                    <div className="stat-label">Citas hoy</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#D1FAE5', color: '#16A34A' }}><CheckCircle size={20} /></div>
                    <div className="stat-value">{loading ? '...' : confirmed}</div>
                    <div className="stat-label">Confirmadas</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#FEE2E2', color: '#DC2626' }}><XCircle size={20} /></div>
                    <div className="stat-value">{loading ? '...' : cancelled}</div>
                    <div className="stat-label">Canceladas</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#ECFDF5', color: '#0D7C7C' }}><Video size={20} /></div>
                    <div className="stat-value">{loading ? '...' : telehealth}</div>
                    <div className="stat-label">Teleconsultas</div>
                </div>
            </div>

            {/* Doctor Filter + Per-Doctor Summary */}
            {!loading && doctors.length > 1 && (
                <div className="card" style={{ marginBottom: 16, padding: '10px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <Filter size={14} color="var(--text-3)" />
                        <button onClick={() => setSelectedDoctorId('all')}
                            style={{
                                padding: '4px 12px', borderRadius: 16, cursor: 'pointer',
                                fontSize: 12, fontWeight: selectedDoctorId === 'all' ? 700 : 400,
                                border: `1.5px solid ${selectedDoctorId === 'all' ? 'var(--primary)' : 'var(--border)'}`,
                                background: selectedDoctorId === 'all' ? 'var(--primary)' : 'transparent',
                                color: selectedDoctorId === 'all' ? '#fff' : 'var(--text-2)',
                            }}>
                            Todos ({appointments.length})
                        </button>
                        {doctorCounts.map(d => (
                            <button key={d.id} onClick={() => setSelectedDoctorId(d.id)}
                                style={{
                                    padding: '4px 12px', borderRadius: 16, cursor: 'pointer',
                                    fontSize: 12, fontWeight: selectedDoctorId === d.id ? 700 : 400,
                                    border: `1.5px solid ${selectedDoctorId === d.id ? d.calendarColor : 'var(--border)'}`,
                                    background: selectedDoctorId === d.id ? `${d.calendarColor}15` : 'transparent',
                                    color: selectedDoctorId === d.id ? d.calendarColor : 'var(--text-2)',
                                    display: 'flex', alignItems: 'center', gap: 4,
                                }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.calendarColor, display: 'inline-block' }} />
                                {d.firstName} ({d.count})
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="card">
                <h3>Agenda de hoy ({filtered.length} citas — {completed} completadas)</h3>
                <table className="table">
                    <thead>
                        <tr>
                            <th>Hora</th>
                            <th>Paciente</th>
                            <th>Médico</th>
                            <th>Tipo</th>
                            <th>Motivo</th>
                            <th>Estado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            Array.from({ length: 4 }).map((_, i) => (
                                <tr key={i}>
                                    {Array.from({ length: 6 }).map((_, j) => (
                                        <td key={j}><div style={{ height: 16, background: 'var(--bg-2)', borderRadius: 4 }} /></td>
                                    ))}
                                </tr>
                            ))
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)' }}>
                                    No hay citas para hoy
                                </td>
                            </tr>
                        ) : (
                            filtered.map(a => {
                                const sc = statusConfig[a.status] || { label: a.status, cls: 'badge-neutral', icon: Clock };
                                const time = new Date(a.scheduledStart).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
                                return (
                                    <tr key={a.id}>
                                        <td style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{time}</td>
                                        <td style={{ fontWeight: 500 }}>{a.patient.firstName} {a.patient.lastName}</td>
                                        <td>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: a.doctor.calendarColor, display: 'inline-block' }} />
                                                {a.doctor.firstName} {a.doctor.lastName}
                                            </span>
                                        </td>
                                        <td>
                                            {a.appointmentType === 'telehealth'
                                                ? <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--primary)' }}><Video size={14} /> Tele</span>
                                                : <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Users size={14} /> Pres.</span>}
                                        </td>
                                        <td style={{ color: 'var(--text-2)', fontSize: 13 }}>{a.chiefComplaint || '—'}</td>
                                        <td><span className={`badge ${sc.cls}`}>{sc.label}</span></td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
